import React, { useState, useEffect } from 'react';
import { HiOutlineDocumentText, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineDownload, HiOutlineSearch, HiOutlineStar, HiOutlineEye, HiOutlineX, HiOutlineRefresh, HiOutlineExclamationCircle } from 'react-icons/hi';
import { resumesService, DeletedResumeWithUsage, CleanupResponse } from '../services/resumesService';
import LoadingSpinner from '../components/LoadingSpinner';
import { Resume, UpdateResumeData } from '../types';
import toast from 'react-hot-toast';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useEscapeModal, hasFormChanges } from '../utils/useEscapeModal';

// Set up the worker for react-pdf 7.x
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface ResumeFormData {
  title: string;
  description: string;
  tags: string;
  isDefault: boolean;
  file?: File;
}

interface DeleteConfirmationData {
  resume: Resume;
  isVisible: boolean;
  usageCount?: number;
  canForceDelete?: boolean;
  showForceConfirmation?: boolean;
}

const Resumes: React.FC = () => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [deletedResumes, setDeletedResumes] = useState<DeletedResumeWithUsage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'active' | 'deleted'>('active');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewResume, setPreviewResume] = useState<Resume | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [editingResume, setEditingResume] = useState<Resume | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [cleaningUp, setCleaningUp] = useState<boolean>(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationData>({
    resume: {} as Resume,
    isVisible: false,
    usageCount: 0,
    canForceDelete: false,
    showForceConfirmation: false
  });

  const [formData, setFormData] = useState<ResumeFormData>({
    title: '',
    description: '',
    tags: '',
    isDefault: false,
    file: undefined
  });

  // Initial form data for change detection
  const [initialFormData, setInitialFormData] = useState<ResumeFormData>({
    title: '',
    description: '',
    tags: '',
    isDefault: false,
    file: undefined
  });

  useEffect(() => {
    fetchResumes();
    if (activeTab === 'deleted') {
      fetchDeletedResumes();
    }
  }, [activeTab]);

  const fetchResumes = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await resumesService.getResumes({ limit: 100 });
      setResumes(response.data);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to fetch resumes');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedResumes = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await resumesService.getDeletedResumes({ limit: 100 });
      setDeletedResumes(response.data);
    } catch (error) {
      console.error('Error fetching deleted resumes:', error);
      toast.error('Failed to fetch deleted resumes');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (resume: Resume): Promise<void> => {
    try {
      setPreviewResume(resume);
      const blob = await resumesService.downloadResume(resume._id);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
      setPageNumber(1);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview');
    }
  };

  const closePreview = (): void => {
    setShowPreview(false);
    setPreviewResume(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    setNumPages(0);
    setPageNumber(1);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      setUploading(true);

      if (editingResume) {
        const updateData: UpdateResumeData = {
          title: formData.title,
          description: formData.description,
          tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
          isDefault: formData.isDefault
        };
        await resumesService.updateResume(editingResume._id, updateData);
        toast.success('Resume updated successfully');
      } else {
        if (!formData.file) {
          toast.error('Please select a file to upload');
          return;
        }
        
        const uploadFormData = new FormData();
        uploadFormData.append('resume', formData.file);
        uploadFormData.append('title', formData.title);
        uploadFormData.append('description', formData.description || '');
        uploadFormData.append('tags', formData.tags || '');
        uploadFormData.append('isDefault', formData.isDefault.toString());
        
        await resumesService.uploadResume(uploadFormData);
        toast.success('Resume uploaded successfully');
      }
      
      setShowModal(false);
      await fetchResumes();
    } catch (error) {
      console.error('Error saving resume:', error);
      toast.error('Failed to save resume');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (resume: Resume): void => {
    setEditingResume(resume);
    const editFormData = {
      title: resume.title || resume.name,
      description: resume.description || '',
      tags: resume.tags ? resume.tags.join(', ') : '',
      isDefault: resume.isDefault || false,
      file: undefined
    };
    setFormData(editFormData);
    setInitialFormData(editFormData);
    setShowModal(true);
  };

  const confirmDelete = (resume: Resume): void => {
    setDeleteConfirmation({
      resume,
      isVisible: true,
      usageCount: 0,
      canForceDelete: false,
      showForceConfirmation: false
    });
  };

  const confirmPermanentDelete = (resume: Resume): void => {
    if (window.confirm(`Are you sure you want to permanently delete "${resume.title || resume.name}"? This will remove it from database and storage forever. This action cannot be undone.`)) {
      performDirectDelete(resume);
    }
  };

  const performDirectDelete = async (resume: Resume): Promise<void> => {
    try {
      const result = await resumesService.deleteResume(resume._id, { hardDelete: true, force: false });
      
      if (result.deletionType === 'hard') {
        toast.success('Resume permanently deleted from database and storage');
      } else {
        toast.success(result.message);
      }
      
      await fetchResumes();
      if (activeTab === 'deleted') {
        await fetchDeletedResumes();
      }
    } catch (error: any) {
      console.error('Error deleting resume:', error);
      
      // Check if this is a usage conflict that can be force deleted
      if (error.response?.data?.canForceDelete) {
        const shouldForceDelete = window.confirm(
          `This resume is being used by ${error.response.data.usageCount} applications. Force delete will permanently remove the resume and remove references from those applications. Continue?`
        );
        
        if (shouldForceDelete) {
          try {
            const forceResult = await resumesService.deleteResume(resume._id, { hardDelete: true, force: true });
            toast.success(`Resume permanently deleted. Removed references from ${forceResult.applicationsAffected ?? 0} applications.`);
            await fetchResumes();
            if (activeTab === 'deleted') {
              await fetchDeletedResumes();
            }
          } catch (forceError) {
            console.error('Error force deleting resume:', forceError);
            toast.error('Failed to force delete resume');
          }
        }
      } else {
        toast.error('Failed to delete resume');
      }
    }
  };

  const handleDelete = async (hardDelete: boolean = false, force: boolean = false): Promise<void> => {
    const { resume } = deleteConfirmation;
    try {
      const result = await resumesService.deleteResume(resume._id, { hardDelete, force });
      
      if (result.deletionType === 'hard') {
        if (result.forceDelete && (result.applicationsAffected ?? 0) > 0) {
          toast.success(`Resume permanently deleted. Removed references from ${result.applicationsAffected ?? 0} applications.`);
        } else {
          toast.success('Resume permanently deleted from database and storage');
        }
      } else {
        toast.success(result.message);
      }
      
      setDeleteConfirmation({ 
        resume: {} as Resume, 
        isVisible: false,
        usageCount: 0,
        canForceDelete: false,
        showForceConfirmation: false
      });
      await fetchResumes();
      if (activeTab === 'deleted') {
        await fetchDeletedResumes();
      }
    } catch (error: any) {
      console.error('Error deleting resume:', error);
      
      // Check if this is a usage conflict that can be force deleted
      if (error.response?.data?.canForceDelete && hardDelete) {
        setDeleteConfirmation(prev => ({
          ...prev,
          usageCount: error.response.data.usageCount,
          canForceDelete: true,
          showForceConfirmation: true
        }));
        return;
      }
      
      if (error.response?.data?.usageCount > 0 && hardDelete) {
        toast.error(`Cannot permanently delete: Used by ${error.response.data.usageCount} applications`);
      } else {
        toast.error('Failed to delete resume');
      }
    }
  };

  const handleRestore = async (id: string): Promise<void> => {
    try {
      await resumesService.restoreResume(id);
      toast.success('Resume restored successfully');
      await fetchDeletedResumes();
      await fetchResumes();
    } catch (error) {
      console.error('Error restoring resume:', error);
      toast.error('Failed to restore resume');
    }
  };

  const handleCleanupOrphaned = async (): Promise<void> => {
    if (!window.confirm('This will permanently delete all soft-deleted resumes that are not used by applications. This action cannot be undone. Continue?')) {
      return;
    }

    try {
      setCleaningUp(true);
      const result: CleanupResponse = await resumesService.cleanupOrphanedResumes();
      
      if (result.cleanedUp > 0) {
        toast.success(`${result.cleanedUp} orphaned resumes permanently deleted`);
      } else {
        toast.success('No orphaned resumes found to cleanup');
      }
      
      if (result.errors && result.errors.length > 0) {
        toast.error(`Some files had errors: ${result.errors.join(', ')}`);
      }
      
      await fetchDeletedResumes();
    } catch (error) {
      console.error('Error cleaning up orphaned resumes:', error);
      toast.error('Failed to cleanup orphaned resumes');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleSetDefault = async (id: string): Promise<void> => {
    try {
      await resumesService.setDefaultResume(id);
      toast.success('Default resume updated');
      await fetchResumes();
    } catch (error) {
      console.error('Error setting default resume:', error);
      toast.error('Failed to set default resume');
    }
  };

  const handleDownload = async (id: string, filename: string): Promise<void> => {
    try {
      const blob = await resumesService.downloadResume(id);
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error('Failed to download resume');
    }
  };

  const resetForm = (): void => {
    const defaultFormData = {
      title: '',
      description: '',
      tags: '',
      isDefault: false,
      file: undefined
    };

    setFormData(defaultFormData);
    setInitialFormData(defaultFormData);
    setEditingResume(null);
    setShowModal(false);
  };

  // Check if form has unsaved changes (excluding file field for comparison)
  const hasUnsavedChanges = hasFormChanges(formData, initialFormData, ['file']);

  // Handle ESC key to close modal with confirmation for unsaved changes
  useEscapeModal({
    isOpen: showModal,
    onClose: resetForm,
    hasUnsavedChanges,
    confirmMessage: 'You have unsaved changes. Are you sure you want to close without saving?'
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType?.includes('pdf')) return 'text-red-600';
    if (mimeType?.includes('word')) return 'text-blue-600';
    return 'text-secondary-600';
  };

  const canPreview = (mimeType: string): boolean => {
    return mimeType?.includes('pdf') || false;
  };

  const currentResumes = activeTab === 'active' ? resumes : deletedResumes;
  const filteredResumes = currentResumes.filter(resume =>
    resume.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resume.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Resumes</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Manage your resume files and track their usage.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'deleted' && deletedResumes.length > 0 && (
            <button
              onClick={handleCleanupOrphaned}
              disabled={cleaningUp}
              className="btn btn-secondary btn-md flex items-center gap-2"
            >
              {cleaningUp ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
              ) : (
                <HiOutlineTrash className="h-4 w-4" />
              )}
              Cleanup Orphaned
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary btn-md flex items-center gap-2"
          >
            <HiOutlinePlus className="h-4 w-4" />
            Upload Resume
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'active'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            Active Resumes ({resumes.length})
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'deleted'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            Deleted Resumes ({deletedResumes.length})
          </button>
        </nav>
      </div>

      {/* Search */}
      <div className="relative">
        <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
        <input
          type="text"
          placeholder="Search resumes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input w-full pl-10"
        />
      </div>

      {/* Resumes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResumes.map((resume) => {
          const isPreviewable = canPreview(resume.mimeType || resume.mimetype);
          const isDeleted = activeTab === 'deleted';
          const deletedResume = isDeleted ? (resume as DeletedResumeWithUsage) : null;
          
          return (
            <div 
              key={resume._id} 
              className={`bg-white rounded-lg border shadow-sm hover:shadow-md transition-all duration-200 group overflow-hidden ${
                isDeleted 
                  ? 'border-red-200 bg-red-50' 
                  : 'border-secondary-200'
              } ${isPreviewable && !isDeleted ? 'cursor-pointer' : ''}`}
              onClick={isPreviewable && !isDeleted ? () => handlePreview(resume) : undefined}
            >
              {/* Card Header */}
              <div className="p-5 pb-3">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <h3 className={`text-lg font-semibold truncate ${
                      isDeleted ? 'text-red-900' : 'text-secondary-900'
                    }`}>
                      {resume.title || resume.name}
                    </h3>
                    {resume.isDefault && !isDeleted && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 shrink-0">
                        <HiOutlineStar className="h-3 w-3 mr-1" />
                        Default
                      </span>
                    )}
                    {isDeleted && deletedResume && deletedResume.usageCount > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 shrink-0">
                        Used by {deletedResume.usageCount} apps
                      </span>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 relative z-10">
                    {!isDeleted && (
                      <>
                        {canPreview(resume.mimeType || resume.mimetype) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(resume);
                            }}
                            className="p-2 rounded-md hover:bg-secondary-100 transition-colors"
                            title="Preview"
                          >
                            <HiOutlineEye className="h-4 w-4 text-secondary-600" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(resume._id, resume.filename);
                          }}
                          className="p-2 rounded-md hover:bg-secondary-100 transition-colors"
                          title="Download"
                        >
                          <HiOutlineDownload className="h-4 w-4 text-secondary-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(resume);
                          }}
                          className="p-2 rounded-md hover:bg-secondary-100 transition-colors"
                          title="Edit"
                        >
                          <HiOutlinePencil className="h-4 w-4 text-secondary-600" />
                        </button>
                        {!resume.isDefault && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDefault(resume._id);
                            }}
                            className="p-2 rounded-md hover:bg-secondary-100 transition-colors"
                            title="Set as default"
                          >
                            <HiOutlineStar className="h-4 w-4 text-secondary-600" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(resume);
                          }}
                          className="p-2 rounded-md hover:bg-red-100 transition-colors"
                          title="Delete"
                        >
                          <HiOutlineTrash className="h-4 w-4 text-red-600" />
                        </button>
                      </>
                    )}
                    
                    {isDeleted && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(resume._id);
                          }}
                          className="p-2 rounded-md hover:bg-green-100 transition-colors"
                          title="Restore"
                        >
                          <HiOutlineRefresh className="h-4 w-4 text-green-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmPermanentDelete(resume);
                          }}
                          className="p-2 rounded-md hover:bg-red-100 transition-colors"
                          title="Permanently Delete"
                        >
                          <HiOutlineTrash className="h-4 w-4 text-red-600" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

            {/* File Preview Area */}
            <div className="px-5 pb-3">
              <div className={`rounded-lg p-6 flex items-center justify-center mb-4 relative ${
                isDeleted ? 'bg-red-100' : 'bg-secondary-50'
              }`}>
                <HiOutlineDocumentText className={`h-16 w-16 ${getFileIcon(resume.mimeType || resume.mimetype)}`} />
                {isPreviewable && !isDeleted && (
                  <div className="absolute top-2 right-2 bg-primary-600 text-white rounded-full p-1">
                    <HiOutlineEye className="h-3 w-3" />
                  </div>
                )}
                {isPreviewable && !isDeleted && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black bg-opacity-20 rounded-lg">
                    <span className="text-white text-sm font-medium bg-black bg-opacity-60 px-3 py-1 rounded-full">
                      Click to preview
                    </span>
                  </div>
                )}
                {isDeleted && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1">
                    <HiOutlineTrash className="h-3 w-3" />
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {resume.description && (
              <div className="px-5 pb-3">
                <p className={`text-sm line-clamp-2 ${
                  isDeleted ? 'text-red-600' : 'text-secondary-600'
                }`}>
                  {resume.description}
                </p>
              </div>
            )}

            {/* Tags */}
            {resume.tags && resume.tags.length > 0 && (
              <div className="px-5 pb-4">
                <div className="flex flex-wrap gap-1">
                  {resume.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        isDeleted 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-primary-100 text-primary-800'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                  {resume.tags.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-secondary-100 text-secondary-600">
                      +{resume.tags.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className={`px-5 py-3 border-t ${
              isDeleted 
                ? 'bg-red-100 border-red-200' 
                : 'bg-secondary-50 border-secondary-100'
            }`}>
              <div className="flex justify-between items-center text-xs text-secondary-500">
                <span>{formatFileSize(resume.fileSize || resume.size || 0)}</span>
                <span>{resume.uploadDate ? new Date(resume.uploadDate).toLocaleDateString() : 'Unknown'}</span>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {filteredResumes.length === 0 && (
        <div className="text-center py-12">
          <HiOutlineDocumentText className="mx-auto h-12 w-12 text-secondary-400" />
          <h3 className="mt-2 text-sm font-medium text-secondary-900">
            {activeTab === 'active' ? 'No resumes found' : 'No deleted resumes found'}
          </h3>
          <p className="mt-1 text-sm text-secondary-500">
            {activeTab === 'active' 
              ? 'Get started by uploading your first resume.' 
              : 'No deleted resumes to show.'}
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            {!deleteConfirmation.showForceConfirmation ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <HiOutlineExclamationCircle className="h-6 w-6 text-orange-600" />
                  <h3 className="text-lg font-medium text-secondary-900">
                    Delete Resume
                  </h3>
                </div>
                
                <p className="text-sm text-secondary-600 mb-4">
                  How would you like to delete "{deleteConfirmation.resume.title || deleteConfirmation.resume.name}"?
                </p>
                
                <div className="space-y-3 mb-6">
                  <div className="p-3 border border-secondary-200 rounded-lg">
                    <h4 className="font-medium text-secondary-900 mb-1">Soft Delete (Recommended)</h4>
                    <p className="text-sm text-secondary-600">
                      Hide the resume but keep it in storage. Can be restored later.
                    </p>
                  </div>
                  
                  <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                    <h4 className="font-medium text-red-900 mb-1">Permanent Delete</h4>
                    <p className="text-sm text-red-600">
                      Permanently remove from database and cloud storage. Cannot be undone.
                      Only available if not used by any applications.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirmation({ 
                      resume: {} as Resume, 
                      isVisible: false,
                      usageCount: 0,
                      canForceDelete: false,
                      showForceConfirmation: false
                    })}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(false)}
                    className="btn btn-warning flex-1"
                  >
                    Soft Delete
                  </button>
                  <button
                    onClick={() => handleDelete(true)}
                    className="btn btn-danger flex-1"
                  >
                    Permanent Delete
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <HiOutlineExclamationCircle className="h-6 w-6 text-red-600" />
                  <h3 className="text-lg font-medium text-red-900">
                    Force Delete Resume?
                  </h3>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800 font-medium mb-2">
                    ⚠️ Are you really sure you want to delete this resume even though {deleteConfirmation.usageCount} applications already use it?
                  </p>
                  <p className="text-sm text-red-700">
                    This will <strong>permanently remove</strong> the resume from storage and 
                    <strong> remove the resume references from {deleteConfirmation.usageCount} applications</strong>. 
                    Those applications will no longer have a resume attached.
                  </p>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                  <p className="text-sm text-yellow-800">
                    <strong>Consequences:</strong>
                  </p>
                  <ul className="text-sm text-yellow-700 mt-1 ml-4 list-disc">
                    <li>Resume file permanently deleted from cloud storage</li>
                    <li>Resume record removed from database</li>
                    <li>{deleteConfirmation.usageCount} applications will lose their resume reference</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirmation(prev => ({
                      ...prev,
                      showForceConfirmation: false,
                      canForceDelete: false
                    }))}
                    className="btn btn-secondary flex-1"
                  >
                    Go Back
                  </button>
                  <button
                    onClick={() => setDeleteConfirmation({ 
                      resume: {} as Resume, 
                      isVisible: false,
                      usageCount: 0,
                      canForceDelete: false,
                      showForceConfirmation: false
                    })}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(true, true)}
                    className="btn btn-danger flex-1"
                  >
                    Force Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upload/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              {editingResume ? 'Edit Resume' : 'Upload New Resume'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input w-full"
                  placeholder="Resume title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="input w-full"
                  placeholder="Brief description of this resume version..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="input w-full"
                  placeholder="e.g. frontend, react, senior (comma-separated)"
                />
              </div>

              {!editingResume && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Resume File
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] })}
                    className="input w-full"
                    required
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Supported formats: PDF, DOC, DOCX (max 10MB)
                  </p>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-secondary-700">
                  Set as default resume
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary flex-1"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={uploading}
                >
                  {uploading 
                    ? 'Processing...' 
                    : editingResume 
                      ? 'Update Resume' 
                      : 'Upload Resume'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPreview && previewResume && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
            {/* Preview Header */}
            <div className="flex justify-between items-center p-4 border-b border-secondary-200">
              <h3 className="text-lg font-medium text-secondary-900">
                {previewResume.title || previewResume.name}
              </h3>
              <div className="flex items-center gap-2">
                {numPages > 1 && (
                  <div className="flex items-center gap-2 text-sm text-secondary-600">
                    <button
                      onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                      disabled={pageNumber <= 1}
                      className="px-2 py-1 rounded border disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span>
                      Page {pageNumber} of {numPages}
                    </span>
                    <button
                      onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                      disabled={pageNumber >= numPages}
                      className="px-2 py-1 rounded border disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
                <button
                  onClick={closePreview}
                  className="p-2 hover:bg-secondary-100 rounded-md"
                >
                  <HiOutlineX className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-4 bg-secondary-50">
              <div className="flex justify-center">
                {canPreview(previewResume.mimeType || previewResume.mimetype) ? (
                  <Document
                    file={previewUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<LoadingSpinner />}
                    error={
                      <div className="text-center py-8">
                        <p className="text-secondary-600">Failed to load PDF preview</p>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={1.0}
                      loading={<LoadingSpinner />}
                    />
                  </Document>
                ) : (
                  <div className="text-center py-12">
                    <HiOutlineDocumentText className="mx-auto h-16 w-16 text-secondary-400 mb-4" />
                    <p className="text-secondary-600">Preview not available for this file type</p>
                    <button
                      onClick={() => handleDownload(previewResume._id, previewResume.filename)}
                      className="btn btn-primary mt-4"
                    >
                      Download to View
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resumes; 